import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ProfileLeftPanel } from "@/components/ProfileLeftPanel.tsx";
import { ProfilePageSkeleton } from "@/components/ProfilePageSkeleton.tsx";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPenToSquare} from "@fortawesome/free-solid-svg-icons";
import {Mentor} from "@/types/Mentor.ts";
import {UserRole} from "@/types/UserRole.ts";
import {Criteria} from "@/types/Criteria.ts";
import {getMentorById} from "@/services/mentorService.ts";
import {getEffectiveDeadlines} from "@/services/deadlineExtensionService.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {adjustTimezone, convertTo12Hour, isPreferenceSubmissionPeriod, isBeforeMentorSelectionDeadline, timezoneResolver} from "@/utils/time-helper.ts";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {useTranslation} from "react-i18next";
import {activeLocale, getLocalizedFieldName, LocalizedField} from "@/utils/translation-helper.ts";
import {Badge} from "@/components/ui/badge.tsx";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import { PageTitle } from "@/components/ui/headings";
import ExpandableText from "@/components/ExpandableText.tsx";

const MentorProfilePage = () => {
    const {t} = useTranslation();
    const {loggedInUser} = useAuth();
    const {id} = useParams(); // Get the mentor ID from route parameters
    const [mentor, setMentor] = useState<Mentor | null>(null);

    const {data: effectiveDeadlines} = useQuery({
        queryKey: queryKeys.effectiveDeadlines,
        queryFn: getEffectiveDeadlines,
        enabled: !!loggedInUser?.id,
    });
    const submitPreferenceDeadline = effectiveDeadlines?.preferenceSubmission
        ? new Date(effectiveDeadlines.preferenceSubmission)
        : undefined;
    const mentorSelectionDeadline = effectiveDeadlines?.mentorSelection
        ? new Date(effectiveDeadlines.mentorSelection)
        : undefined;
    const isAdmin = loggedInUser?.role === UserRole.ADMIN;
    const canEditAvailability = (resourceId: number) => {
        if (!loggedInUser) return false;
        const canEditProfile = isAdmin || (loggedInUser?.mentor?.id ?? -1) === resourceId;
        if (!canEditProfile) return false;
        if (isAdmin) return isBeforeMentorSelectionDeadline(mentorSelectionDeadline);
        return isPreferenceSubmissionPeriod(submitPreferenceDeadline);
    };

    const getLocalizedValue = (key: string) => mentor ? mentor[getLocalizedFieldName(key) as keyof Mentor] as string : key;

    useEffect(() => {
        const fetchMentorData = async () => {
            try {
                // Fetch mentor data based on the mentor ID
                const mentorData = await getMentorById(id ? parseInt(id) : -1);
                setMentor(mentorData);
            } catch (error) {
                console.error("Error fetching mentor data:", error);
            }
        };

        fetchMentorData();
    }, [id]);

    const getListAsString = (criteria: Criteria[]) => {
        if (!criteria || criteria.length === 0) {
            return '-';
        }
        return criteria.map(criterion => criterion[getLocalizedFieldName("name") as keyof Criteria]).join(', ');
    }

    const canEdit = (resourceId: number) => {
        return loggedInUser && (loggedInUser.role === UserRole.ADMIN || (loggedInUser?.mentor?.id ?? -1) === resourceId);
    }

    return (
        <>
            {mentor ? (
                <div className="container pt-8 pb-12">
                    <div className="mx-auto max-w-4xl">
                        <header className="mb-8">
                            <PageTitle>{t("profile.mentorProfile")}</PageTitle>
                            <p className="mt-1 text-sm text-text-2">
                                {t("profile.mentorProfileDesc", "View and manage mentor information.")}
                            </p>
                        </header>
                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr] lg:items-start">
                        <aside className="min-w-0 space-y-4">
                            <ProfileLeftPanel user={{...mentor.user, role: UserRole.MENTOR}}/>
                            <div className="rounded-xl border border-border bg-bg-card px-4 py-3 shadow-sm">
                                <p className="text-xs font-medium uppercase tracking-wide text-text-3">
                                    {t("criteria.affinityGroup")}
                                </p>
                                <Badge
                                    variant="secondary"
                                    className="mt-2 w-fit border-accent/30 bg-accent/10 px-3 py-1 text-text-1 font-medium"
                                >
                                    {mentor.affinityGroup
                                        ? mentor.affinityGroup[getLocalizedFieldName("name") as keyof typeof mentor.affinityGroup]
                                        : t("common.none", "None")}
                                </Badge>
                            </div>
                        </aside>
                        <div className="min-w-0 space-y-6">
                            <Card className="border border-border bg-bg-card shadow-sm">
                                <CardHeader className="border-b border-border">
                                    <div className="flex items-center justify-between gap-4">
                                        <CardTitle className="text-lg font-semibold text-text-1">
                                            {t("profile.generalInformation")}
                                        </CardTitle>
                                        {canEdit(mentor.id) && (
                                            <a href={`/mentors/${mentor.id}/edit/`} className="text-text-2 hover:text-text-1">
                                                <FontAwesomeIcon icon={faPenToSquare}/>
                                            </a>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6 space-y-4">
                                    <ExpandableText text={getLocalizedValue("bio")} maxLength={300} classNames="px-0"/>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="rounded-lg border border-border bg-bg-card px-3 py-3">
                                            <p className="text-xs text-text-3">{t("mentor.title")}</p>
                                            <p className="text-sm font-medium text-text-1">{getLocalizedValue("title")}</p>
                                        </div>
                                        <div className="rounded-lg border border-border bg-bg-card px-3 py-3">
                                            <p className="text-xs text-text-3">{t("mentor.university")}</p>
                                            <p className="text-sm font-medium text-text-1">{getLocalizedValue("university")}</p>
                                        </div>
                                        <div className="rounded-lg border border-border bg-bg-card px-3 py-3">
                                            <p className="text-xs text-text-3">{t("mentor.region")}</p>
                                            <p className="text-sm font-medium text-text-1">{getLocalizedValue("region")}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card className="border border-border bg-bg-card shadow-sm">
                                <CardHeader className="border-b border-border">
                                    <div className="flex items-center justify-between gap-4">
                                        <CardTitle className="text-lg font-semibold text-text-1">
                                            {t("profile.detailsInformation")}
                                        </CardTitle>
                                        {canEdit(mentor.id) && (
                                            <a href={`/mentors/${mentor.id}/edit/details`} className="text-text-2 hover:text-text-1">
                                                <FontAwesomeIcon icon={faPenToSquare}/>
                                            </a>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                            <p className="text-xs text-text-3">{t("mentor.language", {count: mentor.languages.length})}</p>
                                            <p className="text-sm font-medium text-text-1">
                                                {getListAsString(mentor.languages)}
                                            </p>
                                        </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("mentor.discipline", {count: mentor.disciplines.length})}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {getListAsString(mentor.disciplines)}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("criteria.careerFocus")}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {mentor.careerFocus ? mentor.careerFocus[getLocalizedFieldName("name") as keyof Criteria] : "-"}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("criteria.experience")}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {mentor.experience ? mentor.experience[getLocalizedFieldName("name") as keyof Criteria] : "-"}
                                    </p>
                                </div>


                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("mentor.researchExpertise", {count: mentor.researchExpertises.length})}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {getListAsString(mentor.researchExpertises)}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("mentor.socioeconomicDisadvantage", {count: mentor.socioeconomicDisadvantages.length})}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {getListAsString(mentor.socioeconomicDisadvantages)}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">
                                        {mentor.isIndigenous ? `${t("mentor.race_one")} / ${t("mentor.indigeneity")}` : t("mentor.race", {count: mentor.races.length})}
                                    </p>
                                    <p className="text-sm font-medium text-text-1">
                                        {mentor.races.length > 0 ? getListAsString(mentor.races) : mentor.isIndigenous ? t("mentor.indigenous") : mentor.isRacialized === false ? t("mentor.caucasian") : "-"}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("mentor.indigeneity", {count: mentor.indigeneities.length})}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {getListAsString(mentor.indigeneities)}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("mentor.disability", {count: mentor.disabilities.length})}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {getListAsString(mentor.disabilities)}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("criteria.isQueer")}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {mentor.isQueer === true ? t("common.yes") : mentor.isQueer === false ? t("common.no") : "-"}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("criteria.gender")}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {mentor.isQueer ? mentor.gender[getLocalizedFieldName("name") as keyof Criteria] : mentor.isMale ? t("mentor.male") : mentor.isFemale ? t("mentor.female") : "-"}
                                    </p>
                                </div>

                                <div
                                    className="rounded-lg border border-border bg-bg-card px-3 py-4">
                                    <p className="text-xs text-text-3">{t("criteria.sexualOrientation")}</p>
                                    <p className="text-sm font-medium text-text-1">
                                        {mentor.isQueer ? mentor.sexualOrientation[getLocalizedFieldName("name") as keyof Criteria] : mentor.isQueer === false ? t("mentor.straight") : "-"}
                                    </p>
                                </div>
                            </div>
                                </CardContent>
                            </Card>

                            <Card className="border border-border bg-bg-card shadow-sm">
                                <CardHeader className="border-b border-border">
                                    <div className="flex items-center justify-between gap-4">
                                        <CardTitle className="text-lg font-semibold text-text-1">
                                            {t("profile.availabilityInformation")}
                                        </CardTitle>
                                        {canEditAvailability(mentor.id) && (
                                            <a href={`/mentors/${mentor.id}/edit/availability`} className="text-text-2 hover:text-text-1">
                                                <FontAwesomeIcon icon={faPenToSquare}/>
                                            </a>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-6">
                                    <div className="grid gap-4 text-sm text-text-2">
                                        <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
                                            <div className="font-medium text-text-1">
                                                {t("mentor.mentorshipSessions")}
                                                {mentor.isOnWhatsApp && (
                                                    <div className="mt-2">
                                                        <Badge variant="success">{t("mentor.availableOnWhatsApp")}</Badge>
                                                    </div>
                                                )}
                                            </div>
                                            <div>
                                                <ul className="space-y-1">
                                                    {mentor.availabilities?.sort((a, b) => new Date(a.availableOn).getTime() - new Date(b.availableOn).getTime())
                                                        .map((a, idx) => {
                                                            return (
                                                                <li key={idx}>
                                                                    {adjustTimezone(a.availableOn).toDateString()}, {convertTo12Hour(a.availableFrom)} to {convertTo12Hour(a.availableTill)}
                                                                </li>
                                                            )
                                                        })}
                                                </ul>
                                            </div>
                                        </div>
                                        <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
                                            <div className="font-medium text-text-1">{t("common.timezone")}</div>
                                            <div>{mentor.timezone ? timezoneResolver[mentor.timezone][activeLocale() as keyof LocalizedField] : undefined}</div>
                                        </div>
                                        <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
                                            <div className="font-medium text-text-1">{t("mentor.personalMeetingLink")}</div>
                                            <div>
                                                {mentor.personalMeetingLink ? (
                                                    <a className="text-text-1 underline-offset-4 hover:underline" href={mentor.personalMeetingLink} target="_blank">
                                                        {mentor.personalMeetingLink}
                                                    </a>
                                                ) : undefined}
                                            </div>
                                        </div>
                                        {canEdit(mentor.id) && (
                                            <div className="grid gap-2 sm:grid-cols-[160px_1fr]">
                                                <div className="font-medium text-text-1">{t("common.phoneNumber")}</div>
                                                <div>{mentor.phoneNumber}</div>
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                        </div>
                    </div>
                </div>
            ) : (
                <ProfilePageSkeleton />
            )}
        </>
    )
}

export default MentorProfilePage;
